import React, { useEffect, useMemo, useRef, useState } from "react";
import config from "../../config";
import getChangeColumnDefs from "../../utils/Change/changecolumnDefs";
import { ChangeData } from "../../utils/Change/changeTypes";
import getIncidentColumnDefs from "../../utils/Incident/incidentColumnDefs";
import { IncidentData } from "../../utils/Incident/incidentTypes";
import Grid from "../Grid/Grid";
import TaskModal from "../Modal/TaskModal";
import { authFetch } from "../../utils/Security/authFetch";
import getRmaColumnDefs from "../../utils/Rma/rmaColumnDefs";
import getKnowledgeColumnDefs from "../../utils/KnowledgeBase/knowledgeBaseColumnDefs";
import getProblemColumnDefs from "../../utils/Problem/problemColumnDefs";
import getTaskColumnDefs from "../../utils/Task/taskColumnDefs";
import { RmaData } from "../../utils/Rma/rmaTypes";
import { KnowledgeBaseData } from "../../utils/KnowledgeBase/KnowledgeBaseTypes";
import { ProblemData } from "../../utils/Problem/problemTypes";
import { TaskData } from "../../utils/Task/taskTypes";
import { loadLookupIncidents, loadLookupTasks } from "../../utils/lookupData";

type TaskGridLookupModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (
        items: (
            | IncidentData
            | ChangeData
            | RmaData
            | KnowledgeBaseData
            | ProblemData
            | TaskData
            )[],
    ) => void;
    selectedItems?: (
        | IncidentData
        | ChangeData
        | RmaData
        | KnowledgeBaseData
        | ProblemData
        | TaskData
        )[];
};

type RelatedItem =
    | IncidentData
    | ChangeData
    | RmaData
    | KnowledgeBaseData
    | ProblemData
    | TaskData;

/**
 * Turns a stored relatedobject value (array or comma-separated string of
 * ticket IDs) into minimal items the lookup can pre-select.
 */
export const toRelatedItems = (relatedobject: unknown): RelatedItem[] => {
    const ids = Array.isArray(relatedobject)
        ? relatedobject
        : String(relatedobject ?? "").split(",");
    return ids
        .map((id) => String(id ?? "").trim())
        .filter(Boolean)
        .map((ticketId) => ({ ticketId }) as unknown as RelatedItem);
};

const TaskGridLookupModal: React.FC<TaskGridLookupModalProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     onSelect,
                                                                     selectedItems = [],
                                                                 }) => {
    const isForPetrobras =
        localStorage.getItem("isForPetrobras") === "true";

    const [activeTab, setActiveTab] = useState<
        "incident" | "change" | "rma" | "knowledgebase" | "problem" | "task"
    >(isForPetrobras ? "incident" : "change");
    const [incidentData, setIncidentData] = useState<IncidentData[]>([]);
    const [changeData, setChangeData] = useState<ChangeData[]>([]);
    const [rmaData, setRmaData] = useState<RmaData[]>([]);
    const [knowledgebaseData, setKnowledgebaseData] = useState<
        KnowledgeBaseData[]
    >([]);
    const [problemData, setProblemData] = useState<ProblemData[]>([]);
    const [taskData, setTaskData] = useState<TaskData[]>([]);

    const [loading, setLoading] = useState(false);
    const gridApiRef = useRef<any>(null);

    // Selected tickets across every tab, keyed by ticketId
    const [selected, setSelected] = useState<Map<string, RelatedItem>>(
        new Map(),
    );

    // Memoize columnDefs to keep stable references
    const incidentColumnDefs = useMemo(
        () =>
            getIncidentColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );
    const changeColumnDefs = useMemo(
        () =>
            getChangeColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );
    const rmaColumnDefs = useMemo(
        () =>
            getRmaColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );
    const knowledgebaseColumnDefs = useMemo(
        () =>
            getKnowledgeColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );
    const problemColumnDefs = useMemo(
        () =>
            getProblemColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );
    const taskColumnDefs = useMemo(
        () =>
            getTaskColumnDefs(
                () => {},
                () => {},
            ),
        [],
    );

    // Seed the selection from the already-related tickets each time the lookup opens
    useEffect(() => {
        if (!isOpen) return;
        const initial = new Map<string, RelatedItem>();
        selectedItems.forEach((item) => {
            const id = String(item?.ticketId ?? "").trim();
            if (id) initial.set(id, item);
        });
        setSelected(initial);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    useEffect(() => {
        if (!isOpen) return;
        // Stop a load for a closed lookup / previous tab from writing into state
        let cancelled = false;
        const isCancelled = () => cancelled;

        const fetchData = async () => {
            const userId = localStorage.getItem("userid");
            const isAdmin = localStorage.getItem("isAdmin") === "true";

            let url = "";
            switch (activeTab) {
                case "task":
                    // Same loading as TaskView: cached list first, then light batches
                    await loadLookupTasks(setTaskData, isCancelled);
                    break;
                case "change":
                    url = isAdmin
                        ? `${config.apiBaseUrl}/api/changes`
                        : `${config.apiBaseUrl}/api/changes/user/${userId}`;
                    const changeRes = await authFetch(url);
                    setChangeData(await changeRes.json());
                    break;
                case "problem":
                    url = isAdmin
                        ? `${config.apiBaseUrl}/api/problems`
                        : `${config.apiBaseUrl}/api/problems/user/${userId}`;
                    const probRes = await authFetch(url);
                    setProblemData(await probRes.json());
                    break;
                case "incident":
                    // Same loading as IncidentView: cached list, else 2000-row batches
                    await loadLookupIncidents(setIncidentData, isCancelled);
                    break;

                case "knowledgebase":
                    url = isAdmin
                        ? `${config.apiBaseUrl}/api/knowledgeBases`
                        : `${config.apiBaseUrl}/api/knowledgeBases/user/${userId}`;
                    const knowRes = await authFetch(url);
                    setKnowledgebaseData(await knowRes.json());
                    break;
                case "rma":
                    url = isAdmin
                        ? `${config.apiBaseUrl}/api/rmas`
                        : `${config.apiBaseUrl}/api/rmas/user/${userId}`;
                    const rmaRes = await authFetch(url);
                    setRmaData(await rmaRes.json());
                    break;
            }
        };

        setLoading(true);
        fetchData()
            .catch((e) => console.error(`Error loading ${activeTab}:`, e))
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, activeTab]);

    const tabData: Record<typeof activeTab, RelatedItem[]> = {
        incident: incidentData,
        change: changeData,
        rma: rmaData,
        knowledgebase: knowledgebaseData,
        problem: problemData,
        task: taskData,
    };
    const tabColumnDefs = {
        incident: incidentColumnDefs,
        change: changeColumnDefs,
        rma: rmaColumnDefs,
        knowledgebase: knowledgebaseColumnDefs,
        problem: problemColumnDefs,
        task: taskColumnDefs,
    };

    // The grid reports only the rows selected in the active tab: replace that
    // tab's share of the selection and keep the picks from every other tab.
    const handleSelectionChange = (rows: RelatedItem[]) => {
        const tabIds = tabData[activeTab].map((r) => String(r.ticketId));
        setSelected((prev) => {
            const next = new Map(prev);
            tabIds.forEach((id) => next.delete(id));
            rows.forEach((r) => next.set(String(r.ticketId), r));
            return next;
        });
    };

    // Tick the rows that are already related; also re-run as batches arrive
    const preselectRows = (params: { api: any }) => {
        params.api.forEachNode((node: any) => {
            if (
                node.data &&
                !node.isSelected() &&
                selected.has(String(node.data.ticketId))
            ) {
                node.setSelected(true);
            }
        });
    };

    const handleSubmit = () => {
        onSelect(Array.from(selected.values()));
        onClose();
    };

    // Remove a chip: untick it in the grid if it's loaded there, else drop it directly
    const removeSelected = (ticketId: string) => {
        const node = gridApiRef.current?.getRowNode(ticketId);
        if (node?.isSelected()) {
            node.setSelected(false);
        } else {
            setSelected((prev) => {
                const next = new Map(prev);
                next.delete(ticketId);
                return next;
            });
        }
    };

    const tabs: { key: typeof activeTab; label: string }[] = [
        { key: "incident", label: "Incidents" },
        { key: "task", label: "Tasks" },
        ...(isForPetrobras
            ? []
            : ([
                { key: "change", label: "Changes" },
                { key: "rma", label: "RMA" },
                { key: "knowledgebase", label: "Knowledge Base" },
                { key: "problem", label: "Problems" },
            ] as { key: typeof activeTab; label: string }[])),
    ];

    const renderGrid = () => (
        <Grid<any>
            key={activeTab} // force remount on tab change
            rowData={tabData[activeTab]}
            columnDefs={tabColumnDefs[activeTab] as any}
            rowSelection="multiple"
            height="58vh"
            getRowId={(params: { data: { ticketId: any } }) =>
                String(params.data.ticketId)
            }
            onGridReady={(params) => {
                gridApiRef.current = params.api;
            }}
            onFirstDataRendered={preselectRows}
            onModelUpdated={preselectRows}
            onSelectionChanged={handleSelectionChange}
        />
    );

    const count = selected.size;

    return (
        <TaskModal
            isOpen={isOpen}
            onClose={onClose}
            title="Relate Tickets"
            subtitle="Tick the tickets to link from any tab, then confirm."
            footer={
                <>
              <span className="lk-status" style={{ marginRight: "auto" }}>
                {count} selected
              </span>
                    <button type="button" className="lk-btn" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="lk-btn lk-btn-primary"
                        onClick={handleSubmit}
                    >
                        {count
                            ? `Relate ${count} ticket${count > 1 ? "s" : ""}`
                            : "Save"}
                    </button>
                </>
            }
        >
            <div className="lk-tabs" role="tablist">
                {tabs.map((tab) => (
                    <button
                        key={tab.key}
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        className={`lk-tab ${activeTab === tab.key ? "active" : ""}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>

            <div className="lk-selected">
                <span className="lk-selected-label">Selected</span>
                {count === 0 ? (
                    <span className="lk-selected-empty">No tickets selected yet</span>
                ) : (
                    Array.from(selected.keys()).map((ticketId) => (
                        <span key={ticketId} className="lk-chip">
                {ticketId}
                            <button
                                type="button"
                                aria-label={`Remove ${ticketId}`}
                                onClick={() => removeSelected(ticketId)}
                            >
                  ×
                </button>
              </span>
                    ))
                )}
            </div>

            {loading && tabData[activeTab].length === 0 && (
                <div className="lk-status">Loading…</div>
            )}
            <div className="lk-grid">{renderGrid()}</div>
        </TaskModal>
    );
};

export default TaskGridLookupModal;
